import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { url } from '../../../../../env';

const ReplacementUploadSlot = ({ item, onCancelReject, onReplacementComplete }) => {
    const [uploading, setUploading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

    const handleFileSelect = async (file) => {
        if (!file) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Tipo de archivo no permitido. Usa PDF, JPG, PNG o WebP.');
            return;
        }

        setUploading(true);
        try {
            // 1. Get presigned URL
            const presignRes = await axios.post(
                `${url}/document-analysis/items/${item.analysisItemId}/replacement/presign`,
                { filename: file.name, mime_type: file.type },
                { headers }
            );

            const { upload_url, file_key } = presignRes.data;

            // 2. Upload directly to S3
            await axios.put(upload_url, file, {
                headers: { 'Content-Type': file.type },
            });

            // 3. Commit replacement
            const commitRes = await axios.post(
                `${url}/document-analysis/items/${item.analysisItemId}/replacement/commit`,
                { file_key },
                { headers }
            );

            if (commitRes.data.success && onReplacementComplete) {
                onReplacementComplete(item, { new_file_key: commitRes.data.new_file_key });
            }
        } catch (err) {
            console.error('Error uploading replacement:', err);
            alert('Error al subir el archivo de reemplazo.');
        } finally {
            setUploading(false);
        }
    };

    const handleCancelReject = async () => {
        setCancelling(true);
        try {
            await onCancelReject(item);
        } finally {
            setCancelling(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
                backgroundColor: '#ffffff', borderRadius: '1rem', overflow: 'hidden',
                border: '2px dashed #fca5a5', display: 'flex', flexDirection: 'column',
                position: 'relative', minHeight: '220px',
            }}
        >
            {/* Header */}
            <div style={{
                padding: '0.75rem 1.25rem', backgroundColor: '#fef2f2',
                borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '0.5rem'
            }}>
                <i className="pi pi-upload" style={{ fontSize: '1rem', color: '#ef4444' }}></i>
                <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#991b1b' }}>
                    REEMPLAZO REQUERIDO
                </span>
            </div>

            {/* Drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
                style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', padding: '1.5rem', cursor: uploading ? 'wait' : 'pointer',
                    backgroundColor: dragOver ? '#fef2f2' : 'transparent',
                    transition: 'background-color 0.2s',
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                    style={{ display: 'none' }}
                />

                {uploading ? (
                    <>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#0ea5e9', marginBottom: '0.75rem' }}></i>
                        <span style={{ fontSize: '0.875rem', color: '#475569', fontWeight: '500' }}>Subiendo archivo...</span>
                    </>
                ) : (
                    <>
                        <i className="pi pi-cloud-upload" style={{ fontSize: '2rem', color: '#94a3b8', marginBottom: '0.75rem' }}></i>
                        <span style={{ fontSize: '0.875rem', color: '#475569', fontWeight: '500' }}>
                            {item.category}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                            Arrastra o haz clic para subir nuevo archivo
                        </span>
                        <span style={{ fontSize: '0.7rem', color: '#cbd5e1', marginTop: '0.25rem' }}>
                            PDF, JPG, PNG o WebP
                        </span>
                    </>
                )}
            </div>

            {/* Cancel button */}
            <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #e2e8f0' }}>
                <button
                    onClick={(e) => { e.stopPropagation(); handleCancelReject(); }}
                    disabled={cancelling || uploading}
                    style={{
                        width: '100%', padding: '0.5rem', borderRadius: '0.5rem',
                        border: '1px solid #e2e8f0', backgroundColor: '#ffffff', color: '#64748b',
                        fontWeight: '500', fontSize: '0.8rem', cursor: cancelling ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                    }}
                >
                    {cancelling ? (
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.875rem' }}></i>
                    ) : (
                        <i className="pi pi-undo" style={{ fontSize: '0.875rem' }}></i>
                    )}
                    Cancelar rechazo
                </button>
            </div>
        </motion.div>
    );
};

export default ReplacementUploadSlot;
